import { Alert, Box, Button, CircularProgress, Link, Paper, Typography } from "@mui/material";
import * as React from "react";

import config from "../config";
import PageMeta from "../components/PageMeta";
import { ContextStore } from "../store/contextStore";
import { PUBLIC_DOCS_URL, describeDocsFailure, portalNotLive, portalTarget, postDocsHandoff } from "./docsHandoff";
import { DEVELOPER_PATHS } from "../developer/routes";

// The developer documentation lives on the TourOptima developer portal (backend metaplan G4). It
// opens in a new tab through a one-time handoff link: the backend serves the docs on the
// developer host and they cannot be framed here (the public copy this page used to iframe is
// retired at the docs cutover).

const ALLOW_HTTP = (config as any).env === "dev";

type State =
  | { kind: "idle" }
  | { kind: "opening" }
  | { kind: "opened" }
  | { kind: "blocked"; url: string }
  | { kind: "failed"; message: string };

const ImportDocs = (props: any) => {
  const { token, logout } = React.useContext(ContextStore) as any;
  const [state, setState] = React.useState<State>({ kind: "idle" });

  const open = async () => {
    // Opened inside the click, before the request, so browsers do not treat it as a pop-up.
    const tab = window.open("", "_blank");
    if (tab) {
      tab.opener = null;
    }
    setState({ kind: "opening" });
    let result: { status: number; body: any };
    try {
      result = await postDocsHandoff(token);
    } catch (_e) {
      tab?.close();
      setState({ kind: "failed", message: describeDocsFailure("unreachable") });
      return;
    }
    const { status, body } = result;
    if (status === 401) {
      tab?.close();
      logout(DEVELOPER_PATHS.documentation);
      return;
    }
    let url: string | null;
    if (portalNotLive(status, body)) {
      url = PUBLIC_DOCS_URL; // before the docs cutover: the public copy (see docsHandoff.js)
    } else if (status !== 200) {
      tab?.close();
      setState({ kind: "failed", message: describeDocsFailure(body && body.code) });
      return;
    } else {
      url = portalTarget(body && body.url, { allowHttp: ALLOW_HTTP });
    }
    if (!url) {
      tab?.close();
      setState({ kind: "failed", message: describeDocsFailure("unexpected_url") });
      return;
    }
    if (tab && !tab.closed) {
      tab.location.href = url;
      setState({ kind: "opened" });
    } else {
      setState({ kind: "blocked", url });
    }
  };

  return (
    <Box sx={{ m: 2, mt: 0 }}>
      <PageMeta title="Developer documentation" />
      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <Typography variant="h5" gutterBottom>
          Developer documentation
        </Typography>
        <Typography variant="body1" paragraph>
          The API reference, the data exchange format (XML / JSON) and the integration playbook are on the
          TourOptima developer portal. It opens in a new tab, signed in as you for a limited time.
        </Typography>
        <Button
          variant="contained"
          onClick={open}
          disabled={state.kind === "opening"}
          startIcon={state.kind === "opening" ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Open documentation
        </Button>
        {state.kind === "opened" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The documentation opened in a new tab. To open it again later, use this button: the link works only once.
          </Typography>
        )}
        {state.kind === "blocked" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Your browser blocked the new tab.{" "}
            <Link href={state.url} target="_blank" rel="noopener noreferrer">
              Open the documentation
            </Link>{" "}
            (this link works once, within a minute).
          </Alert>
        )}
        {state.kind === "failed" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {state.message}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default ImportDocs;
